import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { shippingApi } from '../services/api';
import {
  COUNTRIES,
  getRegionOptions,
  getRegionLabel,
  getPostalCodeLabel,
  isStateRequired,
  isPostalCodeRequired,
} from '../utils/addressData';
import {
  CalculatorIcon,
  MapPinIcon,
  ArchiveBoxIcon,
  ArrowPathIcon,
  TruckIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface CustomBox {
  id: string;
  length: string;
  width: string;
  height: string;
  weightLb: string;
  weightOz: string;
  quantity: number;
}

interface EstimateRate {
  carrier: string;
  carrier_code: string;
  service_name: string;
  service_code: string;
  total_cost: number;
  delivery_days?: number;
  delivery_date?: string;
  is_third_party?: boolean;
  third_party_account?: { nickname: string; account_number: string } | null;
  num_packages?: number;
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const getCarrierBadgeClass = (carrier: string) => {
  switch ((carrier || '').toLowerCase()) {
    case 'ups':
      return 'bg-amber-100 text-amber-800';
    case 'usps':
    case 'stamps.com':
      return 'bg-blue-100 text-blue-800';
    case 'fedex':
      return 'bg-purple-100 text-purple-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const lbOzToOz = (lb: number, oz: number): number => Math.max(0, lb * 16 + oz);

const newCustomBox = (): CustomBox => ({
  id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  length: '',
  width: '',
  height: '',
  weightLb: '1',
  weightOz: '0',
  quantity: 1,
});

const inputClass =
  'px-2 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue';

export const RatesEstimator: React.FC = () => {
  const [customBoxes, setCustomBoxes] = useState<CustomBox[]>([newCustomBox()]);
  const [toAddress1, setToAddress1] = useState('');
  const [toAddress2, setToAddress2] = useState('');
  const [toZip, setToZip] = useState('');
  const [toState, setToState] = useState('');
  const [toCity, setToCity] = useState('');
  const [toCountry, setToCountry] = useState('US');
  const [stateAutoFilled, setStateAutoFilled] = useState(false);
  const [cityAutoFilled, setCityAutoFilled] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [rates, setRates] = useState<EstimateRate[]>([]);
  const [numBoxes, setNumBoxes] = useState(0);
  const [resolvedState, setResolvedState] = useState<string | null>(null);
  const [stateCorrected, setStateCorrected] = useState(false);
  const [shipFrom, setShipFrom] = useState<{ city: string; state: string; zip: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const totalBoxes = useMemo(
    () => customBoxes.reduce((sum, box) => sum + (box.quantity || 0), 0),
    [customBoxes]
  );

  const lookupZip = useCallback(async (zip: string) => {
    const zipDigits = zip.replace(/\D/g, '').slice(0, 5);
    if (zipDigits.length < 5) return;

    setLookupLoading(true);
    try {
      const result = await shippingApi.lookupZip(zipDigits);
      if (result.state) {
        setToState(result.state);
        setStateAutoFilled(true);
      }
      if (result.city) {
        setToCity(result.city);
        setCityAutoFilled(true);
      }
    } catch (err) {
      console.warn('Zip lookup failed:', err);
      setStateAutoFilled(false);
    } finally {
      setLookupLoading(false);
    }
  }, []);

  // ZIP → city/state auto-fill is a US-only feature
  useEffect(() => {
    if (toCountry !== 'US') {
      setStateAutoFilled(false);
      setCityAutoFilled(false);
      return;
    }
    const zipDigits = toZip.replace(/\D/g, '').slice(0, 5);
    if (zipDigits.length === 5) {
      const timer = setTimeout(() => lookupZip(zipDigits), 400);
      return () => clearTimeout(timer);
    }
    setStateAutoFilled(false);
    setCityAutoFilled(false);
  }, [toZip, toCountry, lookupZip]);

  const isUSDestination = toCountry === 'US';
  const regionOptions = getRegionOptions(toCountry);
  const displayZip = isUSDestination ? toZip.replace(/\D/g, '').slice(0, 5) : toZip.trim();

  const handleCountryChange = (country: string) => {
    setToCountry(country);
    const options = getRegionOptions(country);
    if (options && !options.includes(toState)) {
      setToState('');
    }
    setStateAutoFilled(false);
    setCityAutoFilled(false);
    setResolvedState(null);
    setStateCorrected(false);
  };

  const updateCustomBox = (id: string, patch: Partial<CustomBox>) => {
    setCustomBoxes(prev => prev.map(box => (box.id === id ? { ...box, ...patch } : box)));
  };

  const removeCustomBox = (id: string) => {
    setCustomBoxes(prev => (prev.length <= 1 ? prev : prev.filter(box => box.id !== id)));
  };

  const validateBoxes = (): string | null => {
    let hasActiveBox = false;
    for (const box of customBoxes) {
      if (box.quantity <= 0) continue;
      hasActiveBox = true;
      const length = parseFloat(box.length);
      const width = parseFloat(box.width);
      const height = parseFloat(box.height);
      const weightOz = lbOzToOz(parseInt(box.weightLb, 10) || 0, parseInt(box.weightOz, 10) || 0);
      if (!length || !width || !height) {
        return 'Each box needs length, width, and height.';
      }
      if (weightOz <= 0) {
        return 'Each box needs a weight greater than 0.';
      }
    }
    if (!hasActiveBox) {
      return 'Add at least one box with quantity greater than 0.';
    }
    return null;
  };

  const handleEstimate = async () => {
    setError(null);
    setStateCorrected(false);
    const zipValue = isUSDestination ? toZip.replace(/\D/g, '').slice(0, 5) : toZip.trim();
    if (isUSDestination && zipValue.length < 5) {
      setError('Enter a valid 5-digit destination zip code.');
      return;
    }
    if (!isUSDestination && !zipValue && isPostalCodeRequired(toCountry)) {
      setError('Enter a postal code.');
      return;
    }
    if (!toAddress1.trim()) {
      setError('Enter a street address.');
      return;
    }
    if (!toCity.trim()) {
      setError('Enter a city.');
      return;
    }
    if (isStateRequired(toCountry) && !toState.trim()) {
      setError(`Enter a ${getRegionLabel(toCountry).toLowerCase()}.`);
      return;
    }

    const validationError = validateBoxes();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);
    setHasFetched(false);
    try {
      const custom_boxes = customBoxes
        .filter(box => box.quantity > 0)
        .map(box => ({
          length: parseFloat(box.length),
          width: parseFloat(box.width),
          height: parseFloat(box.height),
          weight_oz: lbOzToOz(parseInt(box.weightLb, 10) || 0, parseInt(box.weightOz, 10) || 0),
          quantity: box.quantity,
        }));

      const result = await shippingApi.estimateRates({
        to_zip: zipValue,
        to_state: toState.trim() || undefined,
        to_city: toCity.trim(),
        to_address_line1: toAddress1.trim(),
        to_address_line2: toAddress2.trim() || undefined,
        to_country: toCountry,
        custom_boxes,
      });

      setRates(result.rates || []);
      setNumBoxes(result.num_boxes || totalBoxes);
      setShipFrom(result.ship_from || null);
      if (result.to_state) {
        setResolvedState(result.to_state);
        setToState(result.to_state);
      }
      setStateCorrected(!!result.state_corrected);
      if (result.to_city) {
        setToCity(result.to_city);
      }
      setHasFetched(true);
    } catch (err: any) {
      console.error('Rate estimate failed:', err);
      setRates([]);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to get rates');
      setHasFetched(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-50 rounded-lg">
              <CalculatorIcon className="w-6 h-6 text-brand-blue" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Rates Estimator</h1>
              <p className="text-sm text-slate-600">
                Get shipping quotes by destination address and box mix — no order required.
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <MapPinIcon className="w-5 h-5 text-slate-400" />
              <h2 className="font-semibold text-slate-900">Destination</h2>
            </div>
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Street address <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={toAddress1}
                  onChange={(e) => setToAddress1(e.target.value)}
                  placeholder="1411 Enterprise Dr"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Apt, suite, etc.
                </label>
                <input
                  type="text"
                  value={toAddress2}
                  onChange={(e) => setToAddress2(e.target.value)}
                  placeholder="Suite 100"
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
                <select
                  value={toCountry}
                  onChange={(e) => handleCountryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                >
                  {COUNTRIES.map(c => (
                    <option key={c.code} value={c.code}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={toCity}
                    onChange={(e) => {
                      setToCity(e.target.value);
                      setCityAutoFilled(false);
                    }}
                    placeholder="Romeoville"
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {getRegionLabel(toCountry)}{isStateRequired(toCountry) && <span className="text-red-500"> *</span>}
                  </label>
                  {regionOptions ? (
                    <select
                      value={toState}
                      onChange={(e) => {
                        setToState(e.target.value);
                        setStateAutoFilled(false);
                      }}
                      className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    >
                      <option value="">--</option>
                      {regionOptions.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={toState}
                      onChange={(e) => {
                        setToState(e.target.value);
                        setStateAutoFilled(false);
                      }}
                      placeholder="Region"
                      className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    />
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    {getPostalCodeLabel(toCountry)}{isPostalCodeRequired(toCountry) && <span className="text-red-500"> *</span>}
                  </label>
                  <input
                    type="text"
                    inputMode={isUSDestination ? 'numeric' : 'text'}
                    maxLength={12}
                    value={toZip}
                    onChange={(e) => setToZip(e.target.value)}
                    placeholder={isUSDestination ? '60446' : 'e.g. V6B 2W9'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                  />
                </div>
              </div>
              {lookupLoading && (
                <p className="text-xs text-slate-500">Looking up city/state from zip…</p>
              )}
              {!lookupLoading && (cityAutoFilled || stateAutoFilled) && toCity && toState && (
                <p className="text-xs text-slate-500">City/state auto-filled from zip — edit if needed.</p>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <ArchiveBoxIcon className="w-5 h-5 text-slate-400" />
              <h2 className="font-semibold text-slate-900">Shipment summary</h2>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-600">Total boxes</span>
                <span className="font-semibold text-slate-900">{totalBoxes}</span>
              </div>
              {resolvedState && (
                <div className="flex justify-between">
                  <span className="text-slate-600">Destination</span>
                  <span className="text-slate-900 text-right max-w-[60%]">
                    {toAddress1}
                    {toAddress2 ? `, ${toAddress2}` : ''}
                    <br />
                    {toCity ? `${toCity}, ` : ''}{resolvedState} {displayZip}
                    {!isUSDestination && <><br />{COUNTRIES.find(c => c.code === toCountry)?.name || toCountry}</>}
                  </span>
                </div>
              )}
              {shipFrom && (
                <div className="flex justify-between pt-2 border-t border-slate-100">
                  <span className="text-slate-600">Ship from</span>
                  <span className="text-slate-900">
                    {shipFrom.city}, {shipFrom.state} {shipFrom.zip}
                  </span>
                </div>
              )}
            </div>
            <button
              onClick={handleEstimate}
              disabled={loading}
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-blue text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Getting rates…
                </>
              ) : (
                <>
                  <TruckIcon className="w-5 h-5" />
                  Get rates
                </>
              )}
            </button>
            {stateCorrected && (
              <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                State was corrected to match the zip code ({resolvedState}).
              </p>
            )}
            {error && (
              <p className="mt-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <h2 className="font-semibold text-slate-900">Boxes</h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Enter dimensions, weight, and quantity for each box type.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setCustomBoxes(prev => [...prev, newCustomBox()])}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-blue border border-brand-blue/30 rounded-lg hover:bg-blue-50"
            >
              <PlusIcon className="w-4 h-4" />
              Add box
            </button>
          </div>

          <div className="divide-y divide-slate-100">
            {customBoxes.map((box) => (
              <div key={box.id} className="px-5 py-4 flex flex-col xl:flex-row xl:items-center gap-4">
                <div className="flex flex-wrap items-center gap-2 flex-1">
                  <span className="text-xs text-slate-500">L</span>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={box.length}
                    onChange={(e) => updateCustomBox(box.id, { length: e.target.value })}
                    placeholder="24"
                    className={`w-16 ${inputClass}`}
                  />
                  <span className="text-xs text-slate-500">W</span>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={box.width}
                    onChange={(e) => updateCustomBox(box.id, { width: e.target.value })}
                    placeholder="24"
                    className={`w-16 ${inputClass}`}
                  />
                  <span className="text-xs text-slate-500">H</span>
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    value={box.height}
                    onChange={(e) => updateCustomBox(box.id, { height: e.target.value })}
                    placeholder="20"
                    className={`w-16 ${inputClass}`}
                  />
                  <span className="text-xs text-slate-500 ml-1">in</span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-slate-500">Weight</span>
                  <input
                    type="number"
                    min={0}
                    value={box.weightLb}
                    onChange={(e) => updateCustomBox(box.id, { weightLb: e.target.value })}
                    className={`w-14 ${inputClass}`}
                  />
                  <span className="text-xs text-slate-500">lb</span>
                  <input
                    type="number"
                    min={0}
                    max={15}
                    value={box.weightOz}
                    onChange={(e) => updateCustomBox(box.id, { weightOz: e.target.value })}
                    className={`w-14 ${inputClass}`}
                  />
                  <span className="text-xs text-slate-500">oz</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Qty</span>
                  <button
                    type="button"
                    onClick={() => updateCustomBox(box.id, { quantity: Math.max(0, box.quantity - 1) })}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min={0}
                    max={99}
                    value={box.quantity}
                    onChange={(e) => updateCustomBox(box.id, { quantity: parseInt(e.target.value, 10) || 0 })}
                    className="w-16 text-center px-2 py-2 border border-slate-300 rounded-lg text-sm font-medium"
                  />
                  <button
                    type="button"
                    onClick={() => updateCustomBox(box.id, { quantity: Math.min(99, box.quantity + 1) })}
                    className="w-9 h-9 flex items-center justify-center rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-100"
                  >
                    +
                  </button>
                  <button
                    type="button"
                    onClick={() => removeCustomBox(box.id)}
                    disabled={customBoxes.length <= 1}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Remove box row"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {hasFetched && !loading && (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-slate-900">Available rates</h2>
                {rates.length > 0 && (
                  <p className="text-sm text-slate-500 mt-0.5">
                    {rates.length} option{rates.length !== 1 ? 's' : ''} for {numBoxes} box{numBoxes !== 1 ? 'es' : ''} to {displayZip}{!isUSDestination ? ` (${toCountry})` : ''}
                  </p>
                )}
              </div>
            </div>

            {rates.length === 0 ? (
              <div className="p-8 text-center text-slate-500 text-sm">
                No rates returned. Check your carrier settings or try a different zip code.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-left text-slate-600">
                      <th className="px-5 py-3 font-medium">Carrier</th>
                      <th className="px-5 py-3 font-medium">Service</th>
                      <th className="px-5 py-3 font-medium">Delivery</th>
                      <th className="px-5 py-3 font-medium text-right">Total cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {rates.map((rate, idx) => (
                      <tr key={`${rate.service_code}-${rate.carrier_code}-${idx}`} className="hover:bg-slate-50/50">
                        <td className="px-5 py-3">
                          <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium uppercase ${getCarrierBadgeClass(rate.carrier_code || rate.carrier)}`}>
                            {(rate.carrier_code || rate.carrier || '').replace(/_/g, ' ')}
                          </span>
                          {rate.is_third_party && rate.third_party_account && (
                            <span className="block text-xs text-slate-500 mt-0.5">
                              {rate.third_party_account.nickname}
                            </span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-slate-900">{rate.service_name || rate.service_code}</td>
                        <td className="px-5 py-3 text-slate-600">
                          {rate.delivery_days != null
                            ? `${rate.delivery_days} day${rate.delivery_days !== 1 ? 's' : ''}`
                            : rate.delivery_date
                              ? new Date(rate.delivery_date).toLocaleDateString()
                              : '—'}
                        </td>
                        <td className="px-5 py-3 text-right font-semibold text-slate-900">
                          {formatCurrency(rate.total_cost || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 text-xs text-slate-500">
              Multi-box totals quote each package and sum by service. Rates should closely match order shipping when the same address and box details are used.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RatesEstimator;
